import ApiV2 from '../../../ApiV2'
import { AppThunk } from '../../../appStore'
import {
    ConnectedPracticeLocation,
    PracticeLocationConfiguration,
    PracticeLocationFeeConfiguration,
    PracticeLocationReschedulingConfiguration,
    PracticeLocationSchedulingConfiguration,
} from '../../../models/v2/Practice'

export enum AppointmentSettingsActionTypes {
    SEARCH_PRACTICE_LOCATIONS_REQUEST = '@V2AppointmentSettings/SEARCH_PRACTICE_LOCATIONS_REQUEST',
    RECEIVE_PRACTICE_LOCATIONS = '@V2AppointmentSettings/RECEIVE_PRACTICE_LOCATIONS',
    SET_DATE_FROM = '@V2AppointmentSettings/SET_DATE_FROM',
    SET_DATE_TO = '@V2AppointmentSettings/SET_DATE_TO',
    SEARCH_RESCHEDULING_CONFIGURATION_REQUEST = '@V2AppointmentSettings/SEARCH_RESCHEDULING_CONFIGURATION_REQUEST',
    RECEIVE_PRACTICE_LOCATION_CANCEL_RESCHEDULE_SETTINGS = '@V2AppointmentSettings/RECEIVE_PRACTICE_LOCATION_CANCEL_RESCHEDULE_SETTINGS',
    RECEIVE_PRACTICE_LOCATION_CANCEL_RESCHEDULE_SETTING = '@V2AppointmentSettings/RECEIVE_PRACTICE_LOCATION_CANCEL_RESCHEDULE_SETTING',
    RECEIVE_PRACTICE_LOCATION_SCHEDULING_SETTINGS = '@V2AppointmentSettings/RECEIVE_PRACTICE_LOCATION_SCHEDULING_SETTINGS',
    RECEIVE_PRACTICE_LOCATION_SCHEDULING_SETTING = '@V2AppointmentSettings/RECEIVE_PRACTICE_LOCATION_SCHEDULING_SETTING',
    RECEIVE_PRACTICE_LOCATION_FEE_SETTINGS = '@V2AppointmentSettings/RECEIVE_PRACTICE_LOCATION_FEE_SETTINGS',
    RECEIVE_CONNECTED_PRACTICE_LOCATIONS = '@V2AppointmentSettings/RECEIVE_CONNECTED_PRACTICE_LOCATIONS',
}

export type ReceivePracticeLocations = {
    type: AppointmentSettingsActionTypes.RECEIVE_PRACTICE_LOCATIONS
    practiceId: string
    practiceLocations: ApiV2.Practice.PracticeLocationFull[]
    pagination: Models.PaginationInfo | undefined
}

export type SetDateFrom = {
    type: AppointmentSettingsActionTypes.SET_DATE_FROM
    practiceId: string
    value: string
    isValid: boolean
    errorMessage: string
}

export type SetDateTo = {
    type: AppointmentSettingsActionTypes.SET_DATE_TO
    practiceId: string
    value: string
    isValid: boolean
    errorMessage: string
}

export type SearchPracticeLocationReschedulingConfigurationRequest = {
    type: AppointmentSettingsActionTypes.SEARCH_RESCHEDULING_CONFIGURATION_REQUEST
    practiceId: string
    page: number
    loading: boolean
}

export type ReceivePracticeLocationAppointmentSettings = {
    type: AppointmentSettingsActionTypes.RECEIVE_PRACTICE_LOCATION_CANCEL_RESCHEDULE_SETTINGS
    practiceId: string
    settings: PracticeLocationReschedulingConfiguration[]
}

export type ReceivePracticeLocationCancelRescheduleSetting = {
    type: AppointmentSettingsActionTypes.RECEIVE_PRACTICE_LOCATION_CANCEL_RESCHEDULE_SETTING
    practiceId: string
    practiceLocationId: string
    setting: PracticeLocationReschedulingConfiguration[]
    isCreate: boolean
}

export type ReceivePracticeLocationSchedulingSettings = {
    type: AppointmentSettingsActionTypes.RECEIVE_PRACTICE_LOCATION_SCHEDULING_SETTINGS
    practiceId: string
    settings: PracticeLocationSchedulingConfiguration[]
}

export type ReceivePracticeLocationSchedulingSetting = {
    type: AppointmentSettingsActionTypes.RECEIVE_PRACTICE_LOCATION_SCHEDULING_SETTING
    practiceId: string
    practiceLocationId: string
    setting: PracticeLocationSchedulingConfiguration
    isCreate: boolean
}

export type ReceivePracticeLocationFeeSettings = {
    type: AppointmentSettingsActionTypes.RECEIVE_PRACTICE_LOCATION_FEE_SETTINGS
    practiceId: string
    settings: PracticeLocationFeeConfiguration[]
}

export type ReceiveConnectedPracticeLocations = {
    type: AppointmentSettingsActionTypes.RECEIVE_CONNECTED_PRACTICE_LOCATIONS
    practiceId: string
    connectedLocations: ConnectedPracticeLocation[]
}

export type SearchPracticeLocationsRequest = {
    type: AppointmentSettingsActionTypes.SEARCH_PRACTICE_LOCATIONS_REQUEST
    practiceId: string
    page: number
    loading: boolean
}

export function receivePracticeLocations(
    practiceId: string,
    practiceLocations: ApiV2.Practice.PracticeLocationFull[],
    pagination: Models.PaginationInfo | undefined,
): ReceivePracticeLocations {
    return {
        type: AppointmentSettingsActionTypes.RECEIVE_PRACTICE_LOCATIONS,
        practiceId,
        practiceLocations,
        pagination,
    }
}

export function setDateFrom(
    practiceId: string,
    value: string,
    isValid: boolean,
    errorMessage: string = '',
): SetDateFrom {
    return {
        type: AppointmentSettingsActionTypes.SET_DATE_FROM,
        practiceId,
        value,
        isValid,
        errorMessage,
    }
}

export function setDateTo(practiceId: string, value: string, isValid: boolean, errorMessage: string = ''): SetDateTo {
    return {
        type: AppointmentSettingsActionTypes.SET_DATE_TO,
        practiceId,
        value,
        isValid,
        errorMessage,
    }
}

export function searchPracticeLocationReschedulingConfigurationRequest(
    practiceId: string,
    page: number,
    loading: boolean,
): SearchPracticeLocationReschedulingConfigurationRequest {
    return {
        type: AppointmentSettingsActionTypes.SEARCH_RESCHEDULING_CONFIGURATION_REQUEST,
        practiceId,
        page,
        loading,
    }
}

export function receivePracticeLocationAppointmentSettings(
    practiceId: string,
    settings: PracticeLocationReschedulingConfiguration[],
): ReceivePracticeLocationAppointmentSettings {
    return {
        type: AppointmentSettingsActionTypes.RECEIVE_PRACTICE_LOCATION_CANCEL_RESCHEDULE_SETTINGS,
        practiceId,
        settings,
    }
}

export function receivePracticeLocationCancelRescheduleSetting(
    practiceId: string,
    practiceLocationId: string,
    setting: PracticeLocationReschedulingConfiguration[],
    isCreate: boolean = false,
): ReceivePracticeLocationCancelRescheduleSetting {
    return {
        type: AppointmentSettingsActionTypes.RECEIVE_PRACTICE_LOCATION_CANCEL_RESCHEDULE_SETTING,
        practiceId,
        practiceLocationId,
        setting,
        isCreate,
    }
}

export function receivePracticeLocationSchedulingSettings(
    practiceId: string,
    settings: PracticeLocationSchedulingConfiguration[],
): ReceivePracticeLocationSchedulingSettings {
    return {
        type: AppointmentSettingsActionTypes.RECEIVE_PRACTICE_LOCATION_SCHEDULING_SETTINGS,
        practiceId,
        settings,
    }
}

export function receivePracticeLocationSchedulingSetting(
    practiceId: string,
    practiceLocationId: string,
    setting: PracticeLocationSchedulingConfiguration,
    isCreate: boolean = false,
): ReceivePracticeLocationSchedulingSetting {
    return {
        type: AppointmentSettingsActionTypes.RECEIVE_PRACTICE_LOCATION_SCHEDULING_SETTING,
        practiceId,
        practiceLocationId,
        setting,
        isCreate,
    }
}

export function receivePracticeLocationFeeSettings(
    practiceId: string,
    settings: PracticeLocationFeeConfiguration[],
): ReceivePracticeLocationFeeSettings {
    return {
        type: AppointmentSettingsActionTypes.RECEIVE_PRACTICE_LOCATION_FEE_SETTINGS,
        practiceId,
        settings,
    }
}

export function receiveConnectedPracticeLocations(
    practiceId: string,
    connectedLocations: ConnectedPracticeLocation[],
): ReceiveConnectedPracticeLocations {
    return {
        type: AppointmentSettingsActionTypes.RECEIVE_CONNECTED_PRACTICE_LOCATIONS,
        practiceId,
        connectedLocations,
    }
}

export function searchPracticeLocationsRequest(
    practiceId: string,
    page: number,
    loading: boolean,
): SearchPracticeLocationsRequest {
    return {
        type: AppointmentSettingsActionTypes.SEARCH_PRACTICE_LOCATIONS_REQUEST,
        practiceId,
        page,
        loading,
    }
}

export function fetchPracticeLocations(practiceId: string, query: string, page: number): any {
    return async (dispatch: any) => {
        await dispatch(searchPracticeLocationsRequest(practiceId, page, true))
        const { data: locations, paginationInfo } = await ApiV2.Practice.getPracticeLocations(practiceId, {
            query,
            page,
            limit: 10,
        })
        await dispatch(receivePracticeLocations(practiceId, locations, paginationInfo))
        return locations
    }
}

// export function fetchPracticeLocationReschedulingConfiguration(
//     practiceId: string,
//     page: number,
// ): AppThunk<Promise<PracticeLocationReschedulingConfiguration[]>> {
//     return async (dispatch: any) => {
//         await dispatch(searchPracticeLocationReschedulingConfigurationRequest(practiceId, page, true))
//         const { data: settings, paginationInfo } = await ApiV2.Practice.getPracticeLocationReschedulingConfiguration(
//             practiceId,
//             page,
//         )
//         await dispatch(receiveAppointmentSettings(practiceId, settings, paginationInfo))
//         return settings
//     }
// }

const mapPracticeLocationConfiguration = (
    configs: PracticeLocationConfiguration[],
): {
    schedulingSettings: PracticeLocationSchedulingConfiguration[]
    reschedulingSettings: PracticeLocationReschedulingConfiguration[]
    feeSettings: PracticeLocationFeeConfiguration[]
} => ({
    schedulingSettings: configs.map(({ practice_location_id, scheduling_configuration }) => ({
        practice_location_id,
        ...scheduling_configuration,
    })),
    reschedulingSettings: configs.map(({ practice_location_id, rescheduling_configuration }) => ({
        practice_location_id,
        ...rescheduling_configuration,
    })),
    feeSettings: configs
        .filter(({ fee_configuration }) => Boolean(fee_configuration))
        .map(({ practice_location_id, fee_configuration }) => ({
            practice_location_id,
            ...fee_configuration,
        })),
})

export function fetchPracticeLocationSchedulingConfiguration(practiceId: string, page: number = 1) {
    return async (dispatch: any) => {
        const configs = await ApiV2.Practice.getPracticeLocationSchedulingConfiguration(practiceId, page)
        const { schedulingSettings, reschedulingSettings, feeSettings } = mapPracticeLocationConfiguration(configs)
        await dispatch(receivePracticeLocationSchedulingSettings(practiceId, schedulingSettings))
        await dispatch(receivePracticeLocationAppointmentSettings(practiceId, reschedulingSettings))
        await dispatch(receivePracticeLocationFeeSettings(practiceId, feeSettings))
        return configs
    }
}

export function updatePracticeLocationSchedulingConfiguration(
    practiceId: string,
    practiceLocationId: string,
    configuration: PracticeLocationSchedulingConfiguration,
    isCreate: boolean = false,
): AppThunk<Promise<PracticeLocationSchedulingConfiguration>> {
    return async (dispatch: any) => {
        const setting = await ApiV2.Practice.putPracticeLocationSchedulingConfiguration(
            practiceId,
            practiceLocationId,
            configuration,
        )

        await dispatch(receivePracticeLocationSchedulingSetting(practiceId, practiceLocationId, setting, isCreate))
        return setting
    }
}

export function updatePracticeLocationReschedulingConfiguration(
    practiceId: string,
    practiceLocationId: string,
    configuration: PracticeLocationReschedulingConfiguration,
    isCreate: boolean = false,
): AppThunk<Promise<PracticeLocationReschedulingConfiguration>> {
    return async (dispatch: any) => {
        const setting = await ApiV2.Practice.putPracticeLocationSchedulingReschedulingConfiguration(
            practiceId,
            practiceLocationId,
            configuration,
        )
        await dispatch(
            receivePracticeLocationCancelRescheduleSetting(practiceId, practiceLocationId, [setting], isCreate),
        )
        return setting
    }
}

export function fetchConnectedPracticeLocations(
    practiceId: string,
    page: number = 1,
): AppThunk<Promise<ConnectedPracticeLocation[]>> {
    return async (dispatch: any) => {
        const connectedLocations = await ApiV2.Practice.getAllConnectedPracticeLocations(practiceId, page)
        await dispatch(receiveConnectedPracticeLocations(practiceId, connectedLocations))
        return connectedLocations
    }
}
